import { uniq } from "@-/com.npmjs.array-utils-ts";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { contentQueries } from "@-/dev.improve.haniverse.database.schemas.content.query";
import type {
  AppGraphemeMeaningCard,
  AppGraphemePronunciationCard,
} from "@-/dev.improve.haniverse.database.schemas.content.schema";
import type { WordCardKind } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { CARD_CONTENT_OVERLAY_TABLES } from "@-/dev.improve.haniverse.lib.cards.card-content";
import { createUseDataloader } from "@-/dev.improve.haniverse.lib.dataloader";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { useCallback } from "react";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export type UserCardAppCard = {
  appGraphemeMeaningCard: AppGraphemeMeaningCard | null;
  appGraphemePronunciationCard: AppGraphemePronunciationCard | null;
  /**
   * The ACTOR-side word overlay. A word card has no content-catalog row (its prompt comes from
   * `sentence` + `sentence_word` at review time), so it is passed through here to keep
   * `resolveAppCardContent` the single place that decides what a card is about.
   *
   * `kind` must be selected: it is the only thing distinguishing the three word cards a single
   * `(word, pinyin)` can have.
   */
  /**
   * The ACTOR-side quiz overlay. Like a word card, a quiz has no content-catalog row: its content
   * ships in the app bundle (the quiz manifest), found by `key`.
   */
  userQuizCard: { key: string } | null;
  userWordSentenceCard: { id: string; kind: WordCardKind; pinyin: string; word: string } | null;
};

// Derived from the card-content registry rather than spelled out, so a new card kind cannot be
// added to some invalidation lists and forgotten here — a stale-cache bug that is invisible until
// a card fails to appear.
const USER_CARD_APP_CARD_TABLES = ["user_card", ...CARD_CONTENT_OVERLAY_TABLES] as const;

/**
 * A pronunciation card IS its `(grapheme, pinyin)` reading: the actor overlay's unique index and
 * every `DrillType` / route key use that pair. The gloss is display copy that content edits
 * rewrite (关 "to shut" → "shut"), so joining on it orphaned every card enrolled under an older
 * gloss: the due scan still counted it, but the scheduler could not resolve it and served new
 * cards as if the queue were empty. Both sides of the join build their key here.
 */
function graphemePronunciationIdentityKey({
  grapheme,
  pinyin,
}: {
  grapheme: string;
  pinyin: string;
}): string {
  return `${grapheme}\0${pinyin}`;
}

// Top-level `useLoader`: reads the actor DB via context and the content reads via
// `contentQueries` (the query client + render-bound key getters captured here),
// and returns a batch-load fn closing over them. `createUseDataloader` shares one
// loader per (actorDb, client, contentDb), including across component instances.
// The content getters bind only that content DB and a fixed config. The content DB
// is immutable, so its reads
// carry the content-query package's `staleTime: Infinity` / finite `gcTime`; the
// two content reads (full-width `AppGrapheme*Card` rows, no `columns` projection)
// are a type-exact match for the column-agnostic `contentQueries`.
function useLoader(cacheContent = true) {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (context) => context.db);
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (context) => context.db);
  const client = useQueryClient();
  const getMeaningCardQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemeMeaningCard.findMany.useGetQueryKey({});
  const getPronunciationCardQueryKey = contentQueries(
    import.meta.location,
  ).appGraphemePronunciationCard.findMany.useGetQueryKey({});
  const batchLoad = useCallback(
    async (userCardIds: readonly string[]): Promise<readonly UserCardAppCard[]> => {
      const uniqueUserCardIds = uniq([...userCardIds]);
      const userCards = await actorDb(import.meta.location).query.userCard.findMany({
        columns: { id: true },
        where: { id: { in: uniqueUserCardIds } },
        with: {
          userGraphemeMeaningCard: {
            columns: { grapheme: true },
          },
          userGraphemePronunciationCard: {
            columns: { grapheme: true, pinyin: true },
          },
          userQuizCard: { columns: { key: true } },
          userWordSentenceCard: {
            columns: { id: true, kind: true, pinyin: true, word: true },
          },
        },
      });

      const meaningGraphemes = uniq(
        userCards.flatMap((userCard) => {
          const userGraphemeMeaningCard = userCard.userGraphemeMeaningCard;
          return userGraphemeMeaningCard === null ? [] : [userGraphemeMeaningCard.grapheme];
        }),
      );
      const pronunciationGraphemes = uniq(
        userCards.flatMap((userCard) => {
          const userGraphemePronunciationCard = userCard.userGraphemePronunciationCard;
          return userGraphemePronunciationCard === null
            ? []
            : [userGraphemePronunciationCard.grapheme];
        }),
      );

      const [appGraphemeMeaningCards, appGraphemePronunciationCards] = await Promise.all([
        hasLength(meaningGraphemes, 0)
          ? []
          : !cacheContent
            ? contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
                where: { grapheme: { in: meaningGraphemes } },
              })
            : client.fetchQuery(
                contentQueries(
                  import.meta.location,
                ).appGraphemeMeaningCard.findMany.getQueryOptions(getMeaningCardQueryKey, {
                  where: { grapheme: { in: meaningGraphemes } },
                }),
              ),
        hasLength(pronunciationGraphemes, 0)
          ? []
          : !cacheContent
            ? contentDb(import.meta.location).query.appGraphemePronunciationCard.findMany({
                where: { grapheme: { in: pronunciationGraphemes } },
              })
            : client.fetchQuery(
                contentQueries(
                  import.meta.location,
                ).appGraphemePronunciationCard.findMany.getQueryOptions(
                  getPronunciationCardQueryKey,
                  { where: { grapheme: { in: pronunciationGraphemes } } },
                ),
              ),
      ]);

      const appGraphemeMeaningCardByGrapheme = new Map(
        appGraphemeMeaningCards.map((appCard) => [appCard.grapheme, appCard]),
      );
      const appGraphemePronunciationCardByIdentity = new Map(
        appGraphemePronunciationCards.map((appCard) => [
          graphemePronunciationIdentityKey(appCard),
          appCard,
        ]),
      );
      const userCardById = new Map(userCards.map((userCard) => [userCard.id, userCard]));

      return userCardIds.map((userCardId): UserCardAppCard => {
        const userCard = userCardById.get(userCardId);
        const userGraphemeMeaningCard = userCard?.userGraphemeMeaningCard;
        const userGraphemePronunciationCard = userCard?.userGraphemePronunciationCard;

        return {
          appGraphemeMeaningCard:
            userGraphemeMeaningCard === null || userGraphemeMeaningCard === undefined
              ? null
              : (appGraphemeMeaningCardByGrapheme.get(userGraphemeMeaningCard.grapheme) ?? null),
          appGraphemePronunciationCard:
            userGraphemePronunciationCard === null || userGraphemePronunciationCard === undefined
              ? null
              : (appGraphemePronunciationCardByIdentity.get(
                  graphemePronunciationIdentityKey(userGraphemePronunciationCard),
                ) ?? null),
          userQuizCard: userCard?.userQuizCard ?? null,
          userWordSentenceCard: userCard?.userWordSentenceCard ?? null,
        };
      });
    },
    [
      actorDb,
      cacheContent,
      client,
      contentDb,
      getMeaningCardQueryKey,
      getPronunciationCardQueryKey,
    ],
  );
  return { batchLoad, cacheIdentity: [actorDb, client, contentDb] as const };
}

const useUserCardAppCardLoader = createUseDataloader({ useLoader });

/**
 * Temporary candidate reads owned by their caller. No per-card or content-batch
 * QueryClient entries are created; callers bound their own batch size and wrap
 * the mutable actor reads in a committed-write guard.
 */
export function useReadUserCardAppCards() {
  return useLoader(false).batchLoad;
}

export function useGetUserCardAppCardQueryKey() {
  const loader = useUserCardAppCardLoader();
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(USER_CARD_APP_CARD_TABLES));

  return (userCardId: string) =>
    withTableChangeVersion(["userCardAppCard", userCardId, loader] as const);
}

export const [useUserCardAppCardQueryOptions, userCardAppCardQueryFn] = createUseQueryOptions(
  useGetUserCardAppCardQueryKey,
  ({ queryKey: [_, userCardId, loader] }) => loader.load(userCardId),
);
